import { camelize } from '@ember/string';
import addMilliseconds from 'date-fns/addMilliseconds';
import addSeconds from 'date-fns/addSeconds';
import addMinutes from 'date-fns/addMinutes';
import addHours from 'date-fns/addHours';
import addDays from 'date-fns/addDays';
import addWeeks from 'date-fns/addWeeks';
import addMonths from 'date-fns/addMonths';
import addYears from 'date-fns/addYears';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInWeeks from 'date-fns/differenceInWeeks';
import differenceInMonths from 'date-fns/differenceInMonths';
import differenceInYears from 'date-fns/differenceInYears';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';

import datesConfig from 'rise-ui/config/dates.js';

const { defaultFormat, formats } = datesConfig;

let u = 0;
const units = {
  default: u,
  ms: ++u,
  secs: ++u,
  mins: ++u,
  hours: ++u,
  days: ++u,
  weeks: ++u,
  months: ++u,
  years: ++u,
};

const parseUnit = unit => units[unit] || units[unit + 's'] || units.default;

const addDefaultUnits = addMilliseconds;

const addByUnit = {
  [units.default]: addDefaultUnits,
  [units.ms]: addMilliseconds,
  [units.secs]: addSeconds,
  [units.mins]: addMinutes,
  [units.hours]: addHours,
  [units.days]: addDays,
  [units.weeks]: addWeeks,
  [units.months]: addMonths,
  [units.years]: addYears,
};

const differenceInDefaultUnits = differenceInMilliseconds;

const differenceByUnit = {
  [units.default]: differenceInDefaultUnits,
  [units.ms]: differenceInMilliseconds,
  [units.secs]: differenceInSeconds,
  [units.mins]: differenceInMinutes,
  [units.hours]: differenceInHours,
  [units.days]: differenceInDays,
  [units.weeks]: differenceInWeeks,
  [units.months]: differenceInMonths,
  [units.years]: differenceInYears,
};

/**
 * Returns integer difference between dates
 * @param {Date} to
 * @param {Date} from
 * @param {string} [unit]
 * @return number
 */
export function diffDates(to, from, unit) {
  return differenceByUnit[ parseUnit(unit) ](to, from);
}

/**
 * Format dates based on application settings
 * @example formatDate(new Date(), 'long')
 * @param {Date} date Date to format
 * @param {string} [dateFormat] Format of result date representation
 * @return {string}
 */
export function formatDate(date, dateFormat) {
  return validDate(date) && format(date, formats[camelize(dateFormat || '')] || formats[defaultFormat]) || '';
}

export function dateChoose(chooser) {
  return (value, min) => {
    let v, m;
    return (m = parseDate(min))
      && (!(v = parseDate(value)) || !chooser(diffDates(v, m)))
        ? min : value;
  };
}

export const dateMin = dateChoose(diff => diff < 0);
export const dateMax = dateChoose(diff => diff > 0);

/**
 * Modify Date by given offset and units
 * @example offsetDate(new Date(), 3, 'days')
 * @param {Date} date
 * @param {number} offset
 * @param {string} [unit]
 * @return {Date|null}
 */
export function offsetDate(date, offset, unit) {
  return validDate(date) && addByUnit[ parseUnit(unit) ](date, offset) || null;
}

/**
 * Parse dates based on application settings
 * @example parseDate(dateString, 'url')
 * @param {string|number|Date} date Date string to be parsed
 * @param {string} [dateFormat] Optional format to force parse from
 * @param {Date} [referenceDate] Date used to fill missing values
 * @return {Date|null}
 */
export function parseDate(date, dateFormat = undefined, referenceDate = new Date()) {
  if (date instanceof Date) {
    return validDate(date);
  }
  const type = typeof date;
  if (!type || type !== 'string' && (type !== 'number' || isNaN(date) || !isFinite(date))) {
    return null;
  }
  let parsed;
  if (dateFormat) {
    return validDate(parse(date, formats[camelize(dateFormat)] || formats[defaultFormat], referenceDate));
  }
  if (type === 'string') {
    if (parsed = validDate(parseISO(date))) {
      return parsed;
    }
  }
  if (parsed = validDate(new Date(date))) {
    return parsed;
  }
  return null;
}

/**
 * Returns valid Date or null
 * @param {Date} date
 * @return {Date|null}
 */
export function validDate(date) {
  return date instanceof Date && !Number.isNaN(date.getTime()) && date || null;
}

/**
 * Offset date to artificially set date to UTC
 * @param {Date} date
 * @return {Date} New Date object
 */
export function utcOffset(date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  );
}
